import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactImageGallery from "react-image-gallery";
import {
  faBed,
  faBath,
  faRulerCombined,
  faClock,
  faPhone,
  faEnvelope,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import placeholder from "../assets/images/house-placeholder.webp";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import InquiryCard from "./inquiryCard.component";
import { useUserParams } from "../hooks/useUserParams";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function Listing() {
  const { id } = useParams();
  const {
    getCurrentAgentName,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentHeadshot,
  } = useUserParams();

  const prop = data.value.find(prop => prop.ListingKey === id); //dummy listings
  const [property, setProperty] = useState(prop); //dummy listings

  // const [property, setProperty] = useState({});
  // const [images, setImages] = useState([]);

  //To create build with dummy listings
  const demoImgs = imgs?.thumbs?.value || [];
  console.log("demoImgs", demoImgs);
  const [images, setImages] = useState(property.Images
    .map(img => ({
      original:
        img,

      thumbnail:
        img
    }))
  );

  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  async function fetchAPI(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };

    const response =
      constants.ENVIRONMENT === "production"
        ?
        await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw new Error("API Error");
    return response.json();
  }

  async function loadListing() {
    try {
      setLoading(true);

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=ListingKey(S)eq(S)'${id}'`
        :
        `https://query.ampre.ca/odata/Property?$filter=ListingKey eq '${id}'`;

      const propertyData =
        await fetchAPI(url);

      setProperty(
        propertyData.value?.[0] || {}
      );

      let mediaUrl;
      if (constants.ENVIRONMENT === "production") {
        mediaUrl =
          `odata/Media(Q)$filter=ResourceRecordKey(S)eq(S)'${id}'(S)and(S)(ImageSizeDescription(S)eq(S)'LargestNoWatermark'(S)or(S)ImageSizeDescription(S)eq(S)'Thumbnail')`;
      } else {
        mediaUrl =
          `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey eq '${id}' and (ImageSizeDescription eq 'LargestNoWatermark' or ImageSizeDescription eq 'Thumbnail')`;
      }

      const media =
        await fetchAPI(
          mediaUrl
        );

      console.log("media", media);

      const formatted =
        media.value?.reduce((acc, item) => {
          const existing =
            acc[item.MediaObjectID] || {};

          acc[item.MediaObjectID] = {
            ...existing,
            [item.ImageSizeDescription]: item
          };
          return acc;
        }, {});

      setImages(
        Object.values(formatted || {})
          .map(img => ({
            original:
              img?.LargestNoWatermark?.MediaURL || placeholder,

            thumbnail:
              img?.Thumbnail?.MediaURL || placeholder
          }))
      );
    }
    catch (err) {
      console.log(err);
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadListing();
  }, []);

  const seoConfig =
    pageSEO.listing(property);

  return (
    <>
      <SEO {...seoConfig} />

      <section className="min-h-screen bg-[#fffdfa] pt-40 pb-24">
        <div className="mx-auto max-w-[1300px] px-6">
          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col lg:flex-row justify-between gap-10 border-b border-gray-200 pb-12"
          >
            <div>
              <p className="uppercase tracking-[0.45em] text-gray-600 mb-8">
                Property Details
              </p>

              <h1 className="mt-6 max-w-3xl font-serif text-5xl leading-tight text-neutral-800 md:text-6xl">
                {property?.StreetNumber}
                {" "}
                {property?.StreetName}
                {" "}
                {property?.StreetSuffix}
              </h1>

              <p className="mt-5 flex items-center gap-3 text-lg text-gray-600">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-accent"
                />

                {property?.City},
                {" "}
                {property?.StateOrProvince}
                {" "}
                {property?.PostalCode}
              </p>
            </div>

            <div className="border border-accent/40 bg-white px-10 py-8 shadow-sm">
              <p className="text-xs uppercase tracking-[0.35em] text-gray-600">
                Listed Price
              </p>

              <h2 className="mt-3 font-serif text-4xl text-neutral-800">
                ${property?.ListPrice?.toLocaleString()}
              </h2>

              <p className="mt-4 text-sm text-gray-600">
                <FontAwesomeIcon
                  icon={faClock}
                  className="mr-2 text-accent"
                />
                Listed{" "}
                {
                  property?.OriginalEntryTimestamp &&
                  new Date(property.OriginalEntryTimestamp)
                    .toLocaleDateString()
                }
              </p>
            </div>
          </motion.div>

          <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_360px]">
            <div className="space-y-10">
              {/* Gallery */}
              <div className="overflow-hidden bg-white p-5 shadow-lg border border-accent/50">
                {
                  loading
                    ?
                    <div className="h-[600px] animate-pulse rounded-lg bg-[#eee9df]" />
                    :
                    <ReactImageGallery
                      items={images}
                      loading="eager"
                      thumbnailPosition={
                        windowSize > 950
                          ?
                          "right"
                          :
                          "bottom"
                      }
                      showThumbnails={
                        windowSize > 950
                      }
                      showPlayButton={false}
                    />
                }
              </div>

              {/* Features */}
              <div className="border border-border-accent/50 bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-serif text-4xl text-neutral-800">
                  Property Features
                </h2>

                <div className="mt-8 grid gap-5 md:grid-cols-3">
                  {
                    property?.PropertyType?.toLowerCase()
                      .includes("residential")
                    &&
                    <>
                      <Feature
                        icon={faBed}
                        title="Bedrooms"
                        text={
                          property?.BedroomsTotal ||
                          "--"
                        }
                      />

                      <Feature
                        icon={faBath}
                        title="Bathrooms"
                        text={
                          property?.BathroomsTotalInteger ||
                          "--"
                        }
                      />
                    </>
                  }

                  <Feature
                    icon={faRulerCombined}
                    title="Size"
                    text={
                      property?.LivingAreaRange ||
                      property?.BuildingAreaTotal ||
                      "--"
                    }
                  />
                </div>
              </div>

              {/* Description */}
              <div className="border border-accent/50 bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-serif text-4xl text-neutral-800">
                  About This Property
                </h2>

                <p className="mt-6 leading-8 text-gray-600">
                  {
                    !detailsOpen &&
                      property?.PublicRemarks?.length > 700
                      ?
                      property.PublicRemarks.substring(0, 700) + "..."
                      :
                      property?.PublicRemarks
                  }
                </p>

                {
                  property?.PublicRemarks?.length > 700 &&
                  <button
                    onClick={() =>
                      setDetailsOpen(!detailsOpen)
                    }
                    className="mt-5 text-sm uppercase tracking-[0.25em] text-gray-600 hover:text-gray-800 transition"
                  >
                    {
                      detailsOpen
                        ?
                        "Show Less"
                        :
                        "Read More..."
                    }
                  </button>
                }
              </div>

              {/* Additional Information */}
              <div className="border border-accent/50 bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-serif text-4xl text-neutral-800">
                  Property Information
                </h2>
                <div className="mt-10 grid gap-x-10 gap-y-6 md:grid-cols-2">
                  <Info
                    label="Property Type"
                    value={property?.PropertyType}
                  />

                  <Info
                    label="Property Sub Type"
                    value={property?.PropertySubType}
                  />

                  <Info
                    label="Heating"
                    value={property?.HeatSource}
                  />

                  <Info
                    label="HVAC"
                    value={property?.HeatType}
                  />

                  <Info
                    label="Garage"
                    value={property?.GarageType}
                  />

                  <Info
                    label="Parking"
                    value={property?.ParkingTotal}
                  />

                  <Info
                    label="Exposure"
                    value={property?.DirectionFaces}
                  />

                  <Info
                    label="Lot Size"
                    value={
                      `${property?.LotSizeRangeAcres || "--"} ${property?.LotSizeUnits || ""}`
                    }
                  />

                  <Info
                    label="Basement"
                    value={
                      property?.Basement?.length
                        ?
                        property.Basement.join(", ")
                        :
                        "--"
                    }
                  />

                  <Info
                    label="Office"
                    value={
                      property?.ListOfficeName
                    }
                  />

                  <Info
                    label="Board"
                    value={
                      property?.ListAOR
                    }
                  />
                </div>
              </div>

              {
                property?.PublicRemarksExtras &&
                <p className="border-accent/50 p-6 text-lg leading-8 text-[#2b2925]">
                  {property.PublicRemarksExtras}
                </p>
              }

              {/* Mortgage Calculator */}
              <div className="border border-accent/50 bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-serif text-4xl text-neutral-800">
                  Affordability Calculator
                </h2>

                <div className="mt-8 overflow-hidden rounded-lg">
                  <iframe
                    src={`https://www.mortgagecalculator.org/webmasters/?downpayment=80000&homevalue=${property?.ListPrice}&loanamount=320000&interestrate=7&loanterm=30&propertytax=2700&pmi=1&homeinsurance=1500&monthlyhoa=0`}
                    scrolling="no"
                    style={{
                      width: "100%",
                      height:
                        windowSize >= 768
                          ?
                          "1800px"
                          :
                          "2200px"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="sticky top-28 h-fit space-y-6">
              <div className="border border-accent/50 bg-white p-8 text-center shadow-sm">
                <div className="relative mx-auto h-36 w-36">
                  <img
                    src={getCurrentHeadshot()}
                    alt={getCurrentAgentName()}
                    className="h-full w-full rounded-full object-cover border-4 border-accent/50"
                  />
                </div>

                <h3 className="mt-6 font-serif text-3xl text-[#2b2925]">
                  {getCurrentAgentName()}
                </h3>

                <p className="mt-2 text-sm uppercase tracking-[0.3em] text-[#6d655b]">
                  Real Estate Advisor
                </p>

                <div className="mt-8 space-y-4 text-sm text-gray-600">
                  <p className="flex items-center justify-center gap-3">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-accent"
                    />
                    {getCurrentAgentNumber()}
                  </p>

                  <p className="flex items-center justify-center gap-3">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-accent"
                    />
                    {getCurrentAgentEmail()}
                  </p>
                </div>
              </div>

              <InquiryCard />
            </aside>
          </div>
        </div>
      </section >
    </>
  );
}

function Feature({
  icon,
  title,
  text
}) {
  return (
    <div className="border border-accent/50 bg-white p-5 transition hover:shadow-lg">
      <FontAwesomeIcon
        icon={icon}
        className="text-2xl text-accent"
      />
      <p className="mt-4 text-xs uppercase tracking-[0.25em] text-gray-600">
        {title}
      </p>

      <p className="mt-2 font-heading2 text-3xl text-neutral-800">
        {text}
      </p>
    </div>
  );
}

function Info({
  label,
  value
}) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-200 pb-3">
      <span className="text-sm uppercase tracking-[0.18em] text-gray-600">
        {label}
      </span>

      <span className="text-right text-sm font-medium text-neutral-800">
        {value || "--"}
      </span>
    </div>
  );
}
